import { AiPanelReport } from '../../../../../types/geneCodes/AiPanelReportTypes';

// ⚠️ THE FLATTENED-CONTAINER FIX FOR ROWS THAT ARE ALREADY STORED.
//
// Five report fields are nested objects in the schema — effects_of_gene_variants {summary,
// traits}, actionable_recommendations {diet, exercise, lifestyle}, clinical_considerations
// {summary, biomarkers, screening_recommendations, doctor_discussion_points},
// performance_biohacking {disclaimer, strategies} and strengths_benefits {summary,
// specific_advantages}. The Anthropic API does not validate tool input against `input_schema`,
// so some rows arrive flattened: the object's first property lands on the field itself as a
// string prefixed with `<parameter name="x">`, and the siblings are promoted to the top level.
// PanelReport reads these as objects, so a flattened row drops all five sections silently.
// This rebuilds the nested shape from the top-level pieces, and skips rows already correct.

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const stripParamTag = (value: string): string =>
	value
		.replace(/^\s*<parameter\b[^>]*>/i, '')
		.replace(/<\/parameter>\s*$/i, '')
		.trim();

const asText = (value: unknown): string | undefined => {
	if (typeof value !== 'string') return undefined;
	const stripped = stripParamTag(value);
	return stripped.length > 0 ? stripped : undefined;
};

// A promoted array may arrive as a real array, or as a JSON array trapped inside a
// `<parameter name="x">` string (the diet case).
const asArray = (value: unknown): unknown[] | undefined => {
	if (Array.isArray(value)) return value;
	if (typeof value === 'string') {
		const stripped = stripParamTag(value);
		if (stripped.startsWith('[')) {
			try {
				const parsed = JSON.parse(stripped);
				if (Array.isArray(parsed)) return parsed;
			} catch {
				return undefined;
			}
		}
	}
	return undefined;
};

const defined = (...values: unknown[]): boolean => values.some((value) => value !== undefined);

export const normalizeReport = (raw: unknown): AiPanelReport => {
	if (!isPlainObject(raw)) return {};

	const src = raw;
	const out: Record<string, unknown> = { ...src };

	if (!isPlainObject(src.effects_of_gene_variants)) {
		const summary = asText(src.effects_of_gene_variants);
		const traits = asArray(src.traits);
		if (defined(summary, traits)) out.effects_of_gene_variants = { summary, traits };
	}

	if (!isPlainObject(src.actionable_recommendations)) {
		const diet = asArray(src.actionable_recommendations);
		const exercise = asArray(src.exercise);
		const lifestyle = asArray(src.lifestyle);
		if (defined(diet, exercise, lifestyle)) out.actionable_recommendations = { diet, exercise, lifestyle };
	}

	if (!isPlainObject(src.clinical_considerations)) {
		const summary = asText(src.clinical_considerations);
		const biomarkers = asArray(src.biomarkers);
		const screening_recommendations = asArray(src.screening_recommendations);
		const doctor_discussion_points = asArray(src.doctor_discussion_points);
		if (defined(summary, biomarkers, screening_recommendations, doctor_discussion_points)) {
			out.clinical_considerations = { summary, biomarkers, screening_recommendations, doctor_discussion_points };
		}
	}

	if (!isPlainObject(src.performance_biohacking)) {
		const disclaimer = asText(src.performance_biohacking);
		const strategies = asArray(src.strategies);
		if (defined(disclaimer, strategies)) out.performance_biohacking = { disclaimer, strategies };
	}

	if (!isPlainObject(src.strengths_benefits)) {
		const summary = asText(src.strengths_benefits);
		const specific_advantages = asArray(src.specific_advantages);
		if (defined(summary, specific_advantages)) out.strengths_benefits = { summary, specific_advantages };
	}

	return out as AiPanelReport;
};
